import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { CheckCircleRounded, ErrorOutlineRounded } from "@mui/icons-material";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { PanelActionFooter, PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { designColors, designType } from "services/constants/designTokens";
import { Field, FormSection, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { SelectField, TextField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { FormulaEditor, PreviewBox, PreviewLine, Required } from "pages/PayRoll/Salary/Shared/salaryUi";
import { FormulaChip, PlainInput, SwitchLine, formatInr } from "pages/PayRoll/Shared/statutoryUi";
import { toDisplayFormula } from "pages/PayRoll/Shared/statutoryConfigData";
import {
  AMOUNT_BASIS_OPTIONS,
  CONTRIBUTION_TYPES,
  FORMULA_FUNCTIONS,
  REQUIRED_CALCULATION_CODES,
  ROUNDING_OPTIONS,
  buildContributionFormula,
  calculationCodesFor,
  checkEsiFormula,
  contributionChipCodesFor,
  finalFormulaOf,
  optionLabel,
  runFormulaOf,
  tableFormulaOf,
} from "./esiConfigData";
import { checkEsiFormulaOnServer, testEsiComponent } from "./esiConfigApi";

// Edit Calculation Component (C1a) and Edit Contribution Component (C1b).
// Calculation components: a formula with pay-component chips (_E = earned
// in the month, no suffix = monthly rate), amount basis and rounding.
// Contribution components: based on, rate and rounding build the formula;
// "advanced" lets the company edit it. The formula is checked in the
// browser while typing and then on the server (POST config/formula/check/,
// which also gives the Final Formula). Test on employee runs the unsaved
// version on the server for one employee and month (nothing is saved).

const emptyComponent = (kind) =>
  kind === "CALCULATION"
    ? { code: "", name: "", kind, description: "", formula: "", amount_basis: "EARNED", rounding: "NONE", used_for: "", status: "Active" }
    : {
        code: "",
        name: "",
        kind,
        description: "",
        type: "EMPLOYEE_DEDUCTION",
        payslip_label: "",
        based_on: "ESI_WAGE",
        rate_pct: "",
        rounding: "UP",
        covered_only: true,
        low_wage_enabled: false,
        low_wage_limit: "",
        advanced: false,
        formula: "",
        used_for: "Deduction",
        status: "Active",
      };

const money = (value) => `₹${formatInr(value)}`;

// Months for Test on employee: this month and the 11 before it.
const testMonths = () =>
  Array.from({ length: 12 }, (_, index) => moment().startOf("month").subtract(index, "months")).map((month) => ({
    value: month.format("YYYY-MM"),
    label: month.format("MMM YYYY"),
  }));

const ValidityLine = ({ check }) => (
  <Mui.Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1 }}>
    {check.valid ? (
      <CheckCircleRounded sx={{ fontSize: 16, color: "#12B76A" }} />
    ) : (
      <ErrorOutlineRounded sx={{ fontSize: 16, color: "#DC2626" }} />
    )}
    <Mui.Typography sx={{ ...designType.captionText, color: check.valid ? "#027A48" : "#DC2626" }}>
      {check.valid ? "Formula valid · all components exist" : check.message}
    </Mui.Typography>
  </Mui.Stack>
);

export const EsiComponentPanel = ({ open, mode, kind, component, form, readOnly, domain, companyId, options, employees, onClose, onSave }) => {
  const [draft, setDraft] = useState(() => component || emptyComponent(kind));
  const [showErrors, setShowErrors] = useState(false);
  const months = useMemo(testMonths, []);
  const [employeeId, setEmployeeId] = useState("");
  const [month, setMonth] = useState(() => months[0].value);
  const [test, setTest] = useState({ loading: false, result: null, error: "" });
  const [serverCheck, setServerCheck] = useState(null);
  const chips = options?.chips;
  const roundingOptions = options?.rounding || ROUNDING_OPTIONS;
  const amountBasisOptions = options?.amount_basis || AMOUNT_BASIS_OPTIONS;
  const typeOptions = options?.contribution_types || CONTRIBUTION_TYPES;
  const functions = chips?.functions?.length ? chips.functions : FORMULA_FUNCTIONS;

  // First employee once the list has loaded.
  useEffect(() => {
    if (!employeeId && employees?.length) setEmployeeId(employees[0].id);
  }, [employees, employeeId]);

  useEffect(() => {
    if (!open) return;
    setDraft(component ? { ...component } : emptyComponent(kind));
    setShowErrors(false);
  }, [open, component, kind]);

  const adding = mode === "add";
  const isContribution = draft.kind === "CONTRIBUTION";
  const required = REQUIRED_CALCULATION_CODES.includes(draft.code) && !adding;
  const locked = readOnly;
  const others = useMemo(() => form.components.filter((item) => adding || item.code !== component?.code), [form.components, adding, component]);
  const calculationOptions = useMemo(
    () => others.filter((item) => item.kind === "CALCULATION").map((item) => ({ value: item.code, label: item.code })),
    [others],
  );

  const set = (key) => (value) =>
    setDraft((current) => {
      const next = { ...current, [key]: key === "code" ? String(value).toUpperCase() : value };
      if (key === "type") next.used_for = value === "EMPLOYEE_DEDUCTION" ? "Deduction" : "Employer cost";
      return next;
    });

  // The formula the component runs: typed for calculation / advanced,
  // otherwise built from the fields.
  const runText = isContribution && !draft.advanced ? buildContributionFormula(draft) : draft.formula;
  const localCheck = useMemo(() => checkEsiFormula(runText, others, draft, chips), [runText, others, draft, chips]);

  // Server check, 0.5 s after the last change. Its answer only counts while
  // the fields it was asked about are unchanged.
  const checkKey = useMemo(() => {
    const body = {
      kind: draft.kind,
      code: draft.code || "NEW",
      rounding: draft.rounding,
      components: others.map((item) => ({ code: item.code, kind: item.kind, formula: runFormulaOf(item) })),
    };
    if (!isContribution) return JSON.stringify({ ...body, formula: draft.formula });
    return JSON.stringify({
      ...body,
      type: draft.type,
      based_on: draft.based_on,
      rate_pct: draft.rate_pct,
      covered_only: Boolean(draft.covered_only),
      low_wage_enabled: draft.type === "EMPLOYEE_DEDUCTION" && Boolean(draft.low_wage_enabled),
      low_wage_limit: draft.type === "EMPLOYEE_DEDUCTION" && draft.low_wage_enabled ? draft.low_wage_limit || null : null,
      advanced: Boolean(draft.advanced),
      ...(draft.advanced ? { formula: draft.formula } : {}),
    });
  }, [draft, others, isContribution]);
  const hasFormula = isContribution && !draft.advanced ? Boolean(draft.based_on && draft.rate_pct) : Boolean(String(draft.formula || "").trim());
  useEffect(() => {
    if (!open || !hasFormula || !domain) return undefined;
    const request = new AbortController();
    const timer = setTimeout(() => {
      checkEsiFormulaOnServer({ domain, companyId, body: JSON.parse(checkKey), signal: request.signal })
        .then((result) => setServerCheck({ key: checkKey, result }))
        .catch(() => {});
    }, 500);
    return () => {
      clearTimeout(timer);
      request.abort();
    };
  }, [open, hasFormula, checkKey, domain, companyId]);
  const server = serverCheck?.key === checkKey ? serverCheck.result : null;
  const formulaCheck = useMemo(
    () => (server ? { ...localCheck, valid: Boolean(server.valid), message: server.message || localCheck.message } : localCheck),
    [server, localCheck],
  );

  const errors = useMemo(() => {
    const result = {};
    if (!String(draft.name).trim()) result.name = "Enter a name";
    else if (/%/.test(draft.name)) result.name = "Keep rates out of the name";
    if (adding) {
      if (!/^[A-Z][A-Z0-9_]{1,19}$/.test(draft.code)) result.code = "2–20 characters: capitals, digits and _ , starting with a letter";
      else if (form.components.some((item) => item.code === draft.code)) result.code = "This code is already used in this version";
    }
    if (isContribution) {
      if (!calculationOptions.some((option) => option.value === draft.based_on)) result.based_on = "Pick a calculation component";
      const rate = Number(draft.rate_pct);
      if (!/^\d+(\.\d{1,2})?$/.test(String(draft.rate_pct)) || !(rate > 0 && rate <= 100)) result.rate_pct = "More than 0, up to 100, 2 decimals";
      if (draft.type === "EMPLOYEE_DEDUCTION" && draft.low_wage_enabled && !(Number(draft.low_wage_limit) > 0)) {
        result.low_wage_limit = "Enter the limit (more than 0)";
      }
    }
    // Field errors first; the built formula is only checked once they pass.
    if (!formulaCheck.valid && (!isContribution || draft.advanced || !Object.keys(result).length)) result.formula = formulaCheck.message;
    return result;
  }, [draft, adding, form.components, isContribution, calculationOptions, formulaCheck]);
  const error = (key) => (showErrors ? errors[key] : undefined);

  const employee = (employees || []).find((item) => item.id === employeeId);
  const runnable = formulaCheck.valid && /^[A-Z][A-Z0-9_]{1,19}$/.test(draft.code) && !errors.rate_pct && !errors.based_on;

  // Test on employee runs by itself (no Run test button): 0.6 s after the
  // employee, month or the component's fields change, the version as it is
  // on screen is run on the server. Only the latest request counts.
  const testKey = runnable && employeeId && month && open && domain ? JSON.stringify([checkKey, draft.code, employeeId, month]) : "";
  useEffect(() => {
    if (!testKey) {
      setTest({ loading: false, result: null, error: "" });
      return undefined;
    }
    const request = new AbortController();
    setTest({ loading: true, result: null, error: "" });
    const timer = setTimeout(() => {
      const own = { ...draft, status: "Active", formula: isContribution && !draft.advanced ? "" : draft.formula };
      const components = adding ? [...form.components, own] : form.components.map((item) => (item.code === component?.code ? own : item));
      testEsiComponent({ domain, companyId, employeeId, month, form: { ...form, components }, componentCode: own.code, signal: request.signal })
        .then((result) => { if (!request.signal.aborted) setTest({ loading: false, result, error: "" }); })
        .catch((err) => { if (!request.signal.aborted) setTest({ loading: false, result: null, error: err.message || "Couldn't run the test." }); });
    }, 600);
    return () => {
      clearTimeout(timer);
      request.abort();
    };
    // testKey covers the fields that change the result.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testKey, form]);

  const toggleAdvanced = (on) =>
    setDraft((current) => ({ ...current, advanced: on, formula: on ? buildContributionFormula(current) : "" }));

  const save = () => {
    setShowErrors(true);
    if (Object.keys(errors).length) return;
    onSave({
      ...draft,
      name: draft.name.trim(),
      formula: isContribution && !draft.advanced ? "" : String(draft.formula).trim(),
      low_wage_enabled: draft.type === "EMPLOYEE_DEDUCTION" ? draft.low_wage_enabled : false,
    });
  };

  const typeWord = isContribution ? "Contribution" : "Calculation";
  const title = adding ? `Add ${typeWord} Component` : `${locked ? "" : "Edit "}${typeWord} Component`;
  const finalText = server?.final_formula ? toDisplayFormula(server.final_formula) : finalFormulaOf({ ...draft, formula: runText, advanced: true });

  const result = test.result;
  const amount = result ? result.component_amount ?? result.result?.[draft.code] : null;
  const testPanel = (
    <PreviewBox>
      {result ? (
        <>
          <Mui.Typography sx={{ ...designType.captionText, mb: 0.5 }}>
            {employee?.name || "Employee"} · {months.find((item) => item.value === month)?.label}
            {result.days_paid != null ? ` · ${Number(result.days_paid)} days paid` : ""}
            {result.covered === false ? " · not covered" : ""}
          </Mui.Typography>
          {result.estimate && result.note ? (
            <Mui.Typography sx={{ ...designType.captionText, color: "#B54708", mb: 0.5 }}>{result.note}</Mui.Typography>
          ) : null}
          {(result.steps || []).map((step, index) => (
            <Mui.Typography key={index} sx={{ ...designType.tableBodyCell, py: 0.25 }}>
              {step}
            </Mui.Typography>
          ))}
          <PreviewLine label={`${draft.code} =`} value={amount == null ? "–" : money(amount)} strong divider />
          {(result.warnings || []).map((warning, index) => (
            <Mui.Typography key={index} sx={{ ...designType.captionText, color: "#B54708", mt: 0.5 }}>
              {warning}
            </Mui.Typography>
          ))}
        </>
      ) : test.loading ? (
        <Mui.Stack direction="row" spacing={1} alignItems="center">
          <Mui.CircularProgress size={16} />
          <Mui.Typography sx={{ ...designType.helperText, color: designColors.slate600 }}>Working it out…</Mui.Typography>
        </Mui.Stack>
      ) : test.error ? (
        <Mui.Typography sx={{ ...designType.helperText, color: "#DC2626" }}>{test.error}</Mui.Typography>
      ) : (
        <Mui.Typography sx={{ ...designType.helperText, color: designColors.slate600 }}>
          {!runnable
            ? "Fill the fields above to see the result here."
            : !employeeId
              ? "Pick an employee to see the result here."
              : "The result shows here."}
        </Mui.Typography>
      )}
    </PreviewBox>
  );

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title={title}
      subtitle={`ESI · ${form.name}`}
      bodySx={panelBodySx}
      footer={
        locked ? (
          <Mui.Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
            <PrimaryButton onClick={onClose}>Close</PrimaryButton>
          </Mui.Stack>
        ) : (
          <PanelActionFooter onCancel={onClose} primaryLabel={adding ? "Add Component" : "Save"} primaryProps={{ onClick: save }} />
        )
      }
    >
      <Mui.Stack spacing={2}>
        {readOnly ? <RequestInfoBanner tone="warning" title="This version is locked." body="A payroll has used it. Create a new version to change components." /> : null}

        <FormSection title="Component Details" info={isContribution ? "Who pays and where it shows." : "A wage base the contributions are worked out on."}>
          <Field
            wide
            label={<>Component Name<Required /></>}
            helper={adding ? "Plain words, no rates (e.g. Employee ESI)." : "Locked: set when the component was added."}
            error={error("name")}
          >
            {/* Like the code, the name can't change once the component is added. */}
            <TextField value={draft.name} onChange={set("name")} disabled={locked || !adding} error={error("name")} placeholder={isContribution ? "e.g. Employee ESI" : "e.g. ESI Contribution Wage"} />
          </Field>
          <Field
            wide
            label={<>Component Code<Required /></>}
            helper={adding ? "Used in formulas. Can't be changed after saving." : "Locked: other formulas use it."}
            error={error("code")}
          >
            <TextField value={draft.code} onChange={set("code")} disabled={!adding} error={error("code")} placeholder={isContribution ? "e.g. EMP_ESI" : "e.g. ESI_WAGE"} />
          </Field>
          {isContribution ? (
            <>
              <Field label={<>Type<Required /></>}>
                <SelectField value={draft.type} onChange={(value) => value && set("type")(value)} options={typeOptions} disabled={locked} />
              </Field>
              <Field label="Payslip label" helper={draft.type === "EMPLOYER_COST" ? "Employer cost: shown in CTC, not on the payslip." : undefined}>
                <TextField value={draft.payslip_label} onChange={set("payslip_label")} disabled={locked} placeholder="e.g. ESI" />
              </Field>
            </>
          ) : (
            <>
              <Field wide label="Description">
                <TextField value={draft.description} onChange={set("description")} disabled={locked} placeholder="What this wage is used for" />
              </Field>
              <Field wide label="Used for" helper="Shown in the components table.">
                <TextField value={draft.used_for} onChange={set("used_for")} disabled={locked} placeholder="e.g. Contribution base (earned, after LOP)" />
              </Field>
            </>
          )}
          <Field wide label="Status" helper={required ? "Required by ESI: always active." : "Inactive components are skipped."}>
            <SwitchLine
              checked={draft.status === "Active"}
              onChange={(checked) => set("status")(checked ? "Active" : "Inactive")}
              disabled={locked || required}
              label={draft.status}
            />
          </Field>
        </FormSection>

        {isContribution ? (
          <FormSection title="Calculation" info="The formula is built from these fields.">
            <Field label={<>Based on<Required /></>} error={error("based_on")}>
              <SelectField value={draft.based_on} onChange={(value) => value && set("based_on")(value)} options={calculationOptions} disabled={locked || draft.advanced} error={error("based_on")} />
            </Field>
            <Field label={<>Rate %<Required /></>} error={error("rate_pct")}>
              <PlainInput type="number" value={draft.rate_pct} onChange={set("rate_pct")} disabled={locked || draft.advanced} error={error("rate_pct")} endAdornment="%" inputProps={{ min: 0, step: 0.01 }} />
            </Field>
            <Field label={<>Rounding<Required /></>}>
              <SelectField value={draft.rounding} onChange={(value) => value && set("rounding")(value)} options={roundingOptions} disabled={locked || draft.advanced} />
            </Field>
            <Mui.Box sx={{ gridColumn: "1 / -1" }}>
              <Mui.Typography sx={{ ...designType.captionText, mb: 0.75 }}>Formula (advanced, optional)</Mui.Typography>
              {draft.advanced ? (
                <FormulaEditor
                  value={draft.formula}
                  onChange={set("formula")}
                  codes={contributionChipCodesFor(others, draft.code)}
                  functions={functions}
                  check={formulaCheck}
                  error={error("formula")}
                  disabled={locked}
                />
              ) : (
                <FormulaChip formula={tableFormulaOf({ ...draft, advanced: false })} />
              )}
              {!locked ? (
                <Mui.Box sx={{ mt: 1 }}>
                  <SwitchLine checked={Boolean(draft.advanced)} onChange={toggleAdvanced} label="Edit formula (only for special cases)" />
                </Mui.Box>
              ) : null}
            </Mui.Box>
          </FormSection>
        ) : (
          <FormSection title="Formula" info="Codes, numbers, + − × ÷, brackets, ROUND, ROUNDUP, ROUNDDOWN, MIN, MAX and IF. Up to 500 characters. _E = earned amount for the month; without _E = monthly rate.">
            <Mui.Box sx={{ gridColumn: "1 / -1" }}>
              <FormulaEditor
                value={draft.formula}
                onChange={set("formula")}
                codes={calculationCodesFor(others, draft.code, chips)}
                functions={functions}
                check={formulaCheck}
                error={error("formula")}
                disabled={locked}
              />
            </Mui.Box>
            <Field label={<>Amount basis<Required /></>} helper="Monthly rate or earned amount.">
              <SelectField value={draft.amount_basis} onChange={(value) => value && set("amount_basis")(value)} options={amountBasisOptions} disabled={locked} />
            </Field>
            <Field label={<>Rounding<Required /></>} helper="Wages are usually not rounded.">
              <SelectField value={draft.rounding} onChange={(value) => value && set("rounding")(value)} options={roundingOptions} disabled={locked} />
            </Field>
          </FormSection>
        )}

        {isContribution ? (
          <FormSection title="Conditions">
            <Mui.Box sx={{ gridColumn: "1 / -1" }}>
              <SwitchLine checked={draft.covered_only} onChange={set("covered_only")} disabled={locked || draft.advanced} label="Only for covered employees" />
              {draft.type === "EMPLOYEE_DEDUCTION" ? (
                <>
                  <SwitchLine
                    checked={draft.low_wage_enabled}
                    onChange={set("low_wage_enabled")}
                    disabled={locked || draft.advanced}
                    label="Low-wage exemption: ₹0 if average daily wage ≤"
                  >
                    <PlainInput
                      type="number"
                      adornment="₹"
                      value={draft.low_wage_limit}
                      onChange={set("low_wage_limit")}
                      disabled={locked || draft.advanced || !draft.low_wage_enabled}
                      error={error("low_wage_limit")}
                      inputProps={{ min: 1, "aria-label": "Low-wage limit per day" }}
                      sx={{ width: 120 }}
                    />
                  </SwitchLine>
                  {error("low_wage_limit") ? (
                    <Mui.Typography sx={{ ...designType.captionText, color: "#DC2626", ml: 7 }}>{error("low_wage_limit")}</Mui.Typography>
                  ) : null}
                  <Mui.Typography sx={{ ...designType.captionText, mt: 0.5 }}>
                    Average daily wage = contribution wage ÷ days paid (2 decimals). Not applied when days paid is 0.
                  </Mui.Typography>
                </>
              ) : (
                <Mui.Typography sx={{ ...designType.captionText, mt: 0.5 }}>The low-wage exemption applies to employee deductions only.</Mui.Typography>
              )}
            </Mui.Box>
          </FormSection>
        ) : null}

        <FormSection title="Final Formula" info={isContribution ? "What payroll runs, conditions included." : `After rounding (${optionLabel(roundingOptions, draft.rounding).toLowerCase()}).`}>
          <Mui.Box sx={{ gridColumn: "1 / -1" }}>
            {/* Nothing typed yet: a hint instead of a lone "–" and an error. */}
            {hasFormula ? (
              <>
                <FormulaChip formula={finalText || "–"} />
                <ValidityLine check={formulaCheck} />
              </>
            ) : (
              <Mui.Typography sx={{ ...designType.helperText, color: error("formula") ? "#DC2626" : designColors.slate600 }}>
                {error("formula") ||
                  (isContribution ? "Fill Based on and Rate % to see the final formula." : "Enter a formula above to see the final formula.")}
              </Mui.Typography>
            )}
          </Mui.Box>
        </FormSection>

        <FormSection
          title="Test on employee"
          info="Preview only — nothing is saved. Uses the payroll of that month when it has run, otherwise an estimate from the monthly rate."
        >
          <Field label="Employee">
            <SelectField
              value={employeeId}
              onChange={(value) => setEmployeeId(value || "")}
              options={(employees || []).map((item) => ({ value: item.id, label: item.code ? `${item.name} · ${item.code}` : item.name }))}
              placeholder={employees == null ? "Loading employees…" : employees.length ? "Pick an employee" : "No employees found"}
            />
          </Field>
          <Field label="Month">
            <SelectField value={month} onChange={(value) => value && setMonth(value)} options={months} />
          </Field>
          <Mui.Box sx={{ gridColumn: "1 / -1" }}>{testPanel}</Mui.Box>
        </FormSection>
      </Mui.Stack>
    </GlobalSidePanel>
  );
};
